import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { Empty, List, Space, Typography } from 'antd';
import BigNumber from 'bignumber.js';
import React, { useContext, useEffect, useState } from 'react';
import { BaseInfoContext } from '@/context/BaseInfoContext';
import AsyncWrapper from '@/components/AsyncWrapper';
import { ID } from '../../global';
import { requestUserBalanceLogList } from '@/models/user';
import { getBalanceLogTypeDesc } from '../../pages/Order/helpers';
import PosButton from '../PosButton';
import Price from '../Price';
import UserBalanceRefund from './UserBalanceRefund';

export type Props = {
  userId: number;
};

const UserOrderList: React.FC<Props> = ({ userId }: Props) => {
  const [, , helpers] = useContext(BaseInfoContext);
  const [refundUserId, setRefundUserId] = useState<ID>();
  const { payload, send, loading, error } = requestUserBalanceLogList();

  const request = (page = 1) => {
    send({
      pathVars: {
        userId: userId,
      },
      params: { limit: 3, page },
    }).then();
  };

  useEffect(() => {
    if (userId) {
      request(1);
    }
  }, [userId]);

  return (
    <>
      <UserBalanceRefund
        userId={refundUserId}
        onClose={() => {
          setRefundUserId(null);
        }}
      />
      <AsyncWrapper loading={loading} error={error} render={!!payload}>
        {() => {
          const list = payload.list;
          const page = payload.page;
          if (!list || !list.length) {
            return <Empty description={'没有账户余额记录'} />;
          }
          return (
            <>
              <List
                loading={loading}
                itemLayout="horizontal"
                size={'small'}
                loadMore={
                  <div className={'space-between'} style={{ paddingTop: 15 }}>
                    <Space size={5}>
                      <PosButton
                        onClick={() => {
                          setRefundUserId(userId);
                        }}>
                        退款销户
                      </PosButton>
                    </Space>

                    <div className={'align-flex-horizontal-center flex-1'}>
                      <PosButton
                        disabled={page.current === 1}
                        loading={loading}
                        style={{ marginRight: 10 }}
                        onClick={() => {
                          request(page.current - 1);
                        }}>
                        上一页
                      </PosButton>
                      <PosButton
                        disabled={page.current === page.pages}
                        loading={loading}
                        style={{ marginLeft: 10 }}
                        onClick={() => {
                          request(page.current + 1);
                        }}>
                        下一页
                      </PosButton>
                    </div>
                  </div>
                }
                dataSource={list}
                renderItem={record => (
                  <List.Item
                    actions={[
                      record.orderSn ? (
                        <PosButton
                          key={'detail'}
                          onClick={() => {
                            helpers.showOrderModal({
                              orderSn: record.orderSn,
                            });
                          }}>
                          订单
                        </PosButton>
                      ) : null,
                    ]}>
                    <List.Item.Meta
                      title={
                        <>
                          {getBalanceLogTypeDesc(record)}{' '}
                          <Typography.Text type={record.value > 0 ? 'danger' : 'success'} strong>
                            {record.value > 0 ? <PlusOutlined /> : <MinusOutlined />}
                          </Typography.Text>
                          <Price
                            value={Math.abs(record.value)}
                            type={record.value > 0 ? 'primary' : 'green'}
                          />
                          {record.cost > 0 && record.value > record.cost && (
                            <Typography.Text style={{ marginLeft: 10, opacity: 0.8 }}>
                              [充
                              <Price value={record.cost} />送
                              <Price
                                value={new BigNumber(record.value).minus(record.cost).toNumber()}
                              />
                              ]
                            </Typography.Text>
                          )}
                        </>
                      }
                      description={
                        <Space direction={'vertical'} size={5}>
                          <div>
                            {record.addTime} 【余额: &yen;{record.after}】
                          </div>
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            </>
          );
        }}
      </AsyncWrapper>
    </>
  );
};

export default UserOrderList;
